import type { Observer } from 'rxjs';
import { Observable } from 'rxjs';

/**
 * Operator similar with `scan` expected that the accumulator must return an observable instead of a value
 * It make possible to reduce subscription messages with async ressources
 * Initially it was created to reduce ressource data with http requests
 *
 * To avoid concurrency errors there is only one subscription running.
 * When a new message is receive from source, if there is a subscription running,
 * the message is stored in `waitingValues` and will be handle when the current subscription is complete
 *
 * Conditions to complete are :
 * - the source is complete
 * - there isn't any waiting values
 */
function concatScan<T, R>(accumulator: (acc: R, value: T) => Observable<R>, seed: R) {
  return (source: Observable<T>): Observable<R> =>
    new Observable((observer: Observer<R>) => {
      let locked = false;
      let completed = false;
      const waitingValues: T[] = [];
      let accumulated: R = seed;

      // we complete main subscription only when there isn't any waiting values
      const handleComplete = () => {
        const waitingValue = waitingValues.shift();

        if (waitingValue) {
          startObservable(waitingValue);
        } else if (completed) {
          observer.complete();
        }
      };

      const startObservable = (value: T) => {
        locked = true;
        accumulator(accumulated, value).subscribe({
          next: value => {
            accumulated = value;
            observer.next(accumulated);
          },
          error: error => {
            locked = false;
            handleComplete();
            console.error(error);
          },
          complete: () => {
            locked = false;
            handleComplete();
          }
        });
      };

      return source.subscribe({
        next: value => {
          // we start observable only if there isn't a subscription running
          if (locked) {
            waitingValues.push(value);
          } else {
            startObservable(value);
          }
        },
        error: error => {
          observer.error(error);
        },
        complete: () => {
          completed = true;
          if (waitingValues.length === 0) {
            observer.complete();
          }
        }
      });
    });
}

export { concatScan };
